import React, {useEffect, useState} from 'react';
import {Link, useHistory} from 'react-router-dom';
import {useDispatch, useSelector} from 'react-redux';
import {Controller, useForm} from 'react-hook-form';
import * as yup from 'yup';
import {yupResolver} from '@hookform/resolvers/yup';
import {authPath, policyPath, rootMainPath} from '../../../routes/paths';
import {postSignUp} from '../../../redux/actions/auth/authActions';
import InputMUI from '../../../shared/InputMUI';
import ButtonMUI from '../../../shared/ButtonMUI';
import CheckboxMUI from '../../../shared/CheckboxMUI';
import Social from '../Social';
import EmailIcon from '../../../assets/icons/email.svg';
import passIcon from '../../../assets/icons/pass.svg';
import UserIcon from '../../../assets/icons/user.svg';
import PhoneIcon from '../../../assets/icons/phone.svg';
import {ReactComponent as InfoIcon} from '../../../assets/icons/info.svg';
import {ReactComponent as BackIcon} from '../../../assets/icons/back.svg';
import employeeAuthBg from '../../../assets/images/auth/Sotrudnik_bg.jpg';
import IconButtonMUI from '../../../shared/IconButtonMUI';
import {useTranslation} from 'react-i18next';

const SignUp = () => {
  const history = useHistory();
  const dispatch = useDispatch();
  const {buttonLoading} = useSelector(({app}) => app);
  const [isCheckboxChecked, setIsCheckboxChecked] = useState(false);
  const {t} = useTranslation();
  useEffect(() => {
    if (localStorage.getItem('token')) {
      history.push(rootMainPath);
    }
  }, [history]);

  const schema = yup.object({
    firstName: yup.string().required(t('validations.required')),
    phone: yup.string().required(t('validations.required')),
    email: yup.string().email(t('validations.email')).required(t('validations.required')),
    password: yup.string().min(8, t('validations.min8')).required(t('validations.required'))
  });

  const {
    control,
    handleSubmit,
    formState: {errors, isValid, isSubmitting}
  } = useForm({
    mode: 'all',
    reValidateMode: 'onChange',
    shouldFocusError: true,
    resolver: yupResolver(schema),
    defaultValues: {
      firstName: '',
      phone: '',
      email: '',
      password: ''
    }
  });

  const onSubmit = async (data) => {
    try {
      data.agreement = true;
      const res = await dispatch(postSignUp(data));

      if (res.payload && res.payload.status === 201) {
        history.push(authPath.successfully);
      }
    } catch (error) {
      console.error('Sign up failed:', error);
    }
  };

  const handleCheckboxChange = (event) => {
    setIsCheckboxChecked(event.target.checked);
  };

  return (
    <div className='auth-page__content'>
      <div className='auth-page__bg'>
        <img className='employee' src={employeeAuthBg} alt='AuthBg' />
      </div>
      <div className='auth-box__wrapper'>
        <div />
        <form className='auth-box' onSubmit={handleSubmit(onSubmit)}>
          <div className='auth-box__head'>
            <div className='auth-box__back'>
              <div className='auth-box__back-icons'>
                <IconButtonMUI color='primary' component={Link} to={authPath.signIn}>
                  <BackIcon />
                </IconButtonMUI>
              </div>
              <div className='auth-box__back-text'>{t('auth.haveAccount')}</div>

              <ButtonMUI variant='text' component={Link} to={authPath.signIn} disableRipple>
                {t('auth.signIn')}
              </ButtonMUI>
            </div>

            <div className='auth-box__subtitle'>{t('auth.registration')}</div>
          </div>

          <div className='auth-box__client'>
            <div className='auth-box__client-icon'>
              <InfoIcon />
            </div>
            <div className='auth-box__client-text'>
              <div className='auth-box__client-descriptions'>{t('auth.registrationAvailable')}</div>
            </div>
          </div>

          <div className='auth-box__input_wrapper'>
            <div className='auth-box__input_box'>
              <Controller
                name='firstName'
                control={control}
                render={({field}) => (
                  <InputMUI
                    className='auth-box__input'
                    type='name'
                    fullWidth
                    error={errors.firstName?.message}
                    inputProps={field}
                    label={t('auth.nameLabel')}
                    startAdornment={UserIcon}
                  />
                )}
              />
            </div>
            <div className='auth-box__input_box'>
              <Controller
                name='phone'
                control={control}
                render={({field, fieldState: {}}) => (
                  <InputMUI
                    className='auth-box__input'
                    type='tel'
                    fullWidth
                    error={errors?.phone?.message}
                    label={'+38'}
                    startAdornment={PhoneIcon}
                    onChange={(e) => {
                      const value = e.target.value;

                      let cleanValue = value.replace(/[^0-9+]/g, ''); // Remove anything that's not a digit or '+'

                      if (cleanValue.charAt(0) !== '+') {
                        cleanValue = cleanValue.replace(/\+/g, ''); // Remove any '+' that is not at the start
                      } else {
                        cleanValue = '+' + cleanValue.replace(/\+/g, ''); // Keep only the first '+' and remove others
                      }

                      if (/^\+?\d*$/.test(cleanValue)) {
                        field.onChange(cleanValue);
                      }
                    }}
                    value={field.value}
                    inputProps={field}
                  />
                )}
              />
            </div>
            <div className='auth-box__input_box'>
              <Controller
                name='email'
                control={control}
                render={({field}) => (
                  <InputMUI
                    className='auth-box__input'
                    type='email'
                    fullWidth
                    error={errors.email?.message}
                    inputProps={field}
                    label={t('auth.emailLabel')}
                    startAdornment={EmailIcon}
                  />
                )}
              />
            </div>
            <div className='auth-box__input_box'>
              <Controller
                name='password'
                control={control}
                render={({field}) => (
                  <InputMUI
                    className='auth-box__input'
                    type='password'
                    fullWidth
                    error={errors.password?.message}
                    inputProps={field}
                    label={t('auth.passwordLabel')}
                    startAdornment={passIcon}
                  />
                )}
              />
            </div>
          </div>

          <div className='auth-box__checkbox'>
            <CheckboxMUI checked={isCheckboxChecked} onChange={handleCheckboxChange} />
            <div className='auth-box__checkbox-text'>
              {t('auth.checkboxText')}
              <a href={policyPath} target='_blank'>
                {t('auth.polytic')}
              </a>
            </div>
          </div>

          <ButtonMUI
            disabled={!isValid || buttonLoading || !isCheckboxChecked || isSubmitting}
            loading={isSubmitting}
            formAction
          >
            {t('auth.createAccount')}
          </ButtonMUI>

          <div className='auth-box__info-text'>
            <div className='text'>{t('auth.registerWith')}</div>
          </div>

          <Social />
        </form>

        <div className='auth-box__subscribe'>{t('SideBgFooter.copyright', {year: new Date().getFullYear()})}</div>
      </div>
    </div>
  );
};

export default SignUp;
